import { CompanyRespondentStatuses, RespondentStatusItem } from '../schemas/RespondentApprovalStatus.schema';
import { VALID_EMAIL_REGEX } from './regex';

// Absent company = not onboarded. Statuses span all firms, so `rejected` is a company-wide denial.
export type RespondentStatusLookup = Map<number, Map<string, RespondentStatusItem>>;

const emailKey = (email: string): string => email.trim().toLowerCase();

export function buildRespondentStatusLookup(companies: CompanyRespondentStatuses[]): RespondentStatusLookup {
  const lookup: RespondentStatusLookup = new Map();
  for (const { companyId, statuses } of companies) {
    const byEmail = new Map<string, RespondentStatusItem>();
    for (const item of statuses) {
      byEmail.set(emailKey(item.email), { email: item.email.trim(), status: item.status });
    }
    lookup.set(companyId, byEmail);
  }
  return lookup;
}

export interface RespondentDisplay {
  onboarded: boolean;
  visible: string[];
  approved: string[];
  pending: string[];
}

export function mergeRespondentDisplay(
  localRespondents: readonly string[] | null | undefined,
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined
): RespondentDisplay {
  if (!companyStatuses || companyStatuses.size === 0) {
    return { onboarded: false, visible: [...(localRespondents ?? [])], approved: [], pending: [] };
  }

  const approved: string[] = [];
  const pending: string[] = [];
  for (const item of companyStatuses.values()) {
    if (item.status === 'approved') {
      approved.push(item.email);
    } else if (item.status === 'pending' || item.status === 'initial') {
      pending.push(item.email);
    }
  }

  return { onboarded: true, visible: [...approved, ...pending], approved, pending };
}

// `rejected` is a company-wide denial, so rejected-only companies count as having no respondents.
export function hasNonRejectedRespondents(
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined
): boolean {
  if (!companyStatuses) {
    return false;
  }
  for (const item of companyStatuses.values()) {
    if (item.status !== 'rejected') {
      return true;
    }
  }
  return false;
}

export interface SuggestedEmailClassification {
  additions: string[];
  rejected: string[];
  invalid: string[];
}

export function classifySuggestedEmails(
  tokens: readonly string[],
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined,
  existing: readonly string[]
): SuggestedEmailClassification {
  const existingKeys = new Set(existing.map(emailKey));
  const additionKeys = new Set<string>();
  const additions: string[] = [];
  const rejected: string[] = [];
  const invalid: string[] = [];

  for (const token of tokens.map((t) => t.trim()).filter(Boolean)) {
    const key = emailKey(token);
    if (companyStatuses?.get(key)?.status === 'rejected') {
      rejected.push(token);
    } else if (!VALID_EMAIL_REGEX.test(token)) {
      invalid.push(token);
    } else if (!existingKeys.has(key) && !additionKeys.has(key)) {
      additions.push(token);
      additionKeys.add(key);
    }
  }

  return { additions, rejected, invalid };
}

export interface SuggestionReconcile {
  chips: string[];
  respondents: string[];
  rejected: string[];
  invalid: string[];
}

export function reconcileSuggestion(
  chips: readonly string[],
  next: readonly string[],
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined,
  respondents: readonly string[]
): SuggestionReconcile {
  const currentKeys = new Set(chips.map(emailKey));
  const nextKeys = new Set(next.map(emailKey));
  const added = next.filter((email) => !currentKeys.has(emailKey(email)));
  const removedKeys = new Set(chips.filter((email) => !nextKeys.has(emailKey(email))).map(emailKey));
  const { additions, rejected, invalid } = classifySuggestedEmails(added, companyStatuses, respondents);

  return {
    chips: chips.filter((email) => !removedKeys.has(emailKey(email))).concat(additions),
    respondents: respondents.filter((email) => !removedKeys.has(emailKey(email))).concat(additions),
    rejected,
    invalid,
  };
}

export function diffNewEmails(next: readonly string[], prev: readonly string[]): string[] {
  const prevKeys = new Set(prev.map(emailKey));
  const seen = new Set<string>();
  const added: string[] = [];
  for (const email of next) {
    const key = emailKey(email);
    if (prevKeys.has(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    added.push(email);
  }
  return added;
}

export interface RespondentStatusChange {
  added: string[];
  removed: string[];
}

// Drop chips the company has since approved/rejected — no longer editable suggestions. Session-typed
// adds have no SB status yet, so they stay.
export function editableChips(
  chips: readonly string[],
  companyStatuses: ReadonlyMap<string, RespondentStatusItem> | undefined
): string[] {
  return chips.filter((email) => {
    const status = companyStatuses?.get(emailKey(email))?.status;
    return status !== 'approved' && status !== 'rejected';
  });
}
